import Button from 'components/Form/Button';
import { Icon, Icons } from 'components/Icon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import male from '../../../../../../../assets/male.png';
import ConfirmModal from 'components/Modal/ConfirmModal';
import CircleProgressBar from 'components/CircleProgressBar';
import {
  useBondAllocationBulkEdit,
  useGetBondDetails,
  useUnAssignBondBeneficiary
} from 'common/queries-and-mutations/bond';
import { useFormik } from 'formik';
import { BeneficiariesAllocationDto } from 'service/dto/general.dto';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { Loader } from 'components/Loader';
import AlertNotification from 'components/AlertNotification';
import { StringFormater } from 'utils';
import withCreatePortal from 'components/HOC/withCreatePortal';

const EnhancedConfirmModal = withCreatePortal(ConfirmModal);

export default function BondAssetAllocation() {
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [selectedAllocationId, setSelectedAllocationId] = useState<
    number | null
  >(null);

  const { bondId } = useParams();

  const {
    data: asset,
    isLoading,
    isFetched,
    refetch
  } = useGetBondDetails({
    bondId: +bondId!
  });

  const { mutate, isSuccess, isError } = useUnAssignBondBeneficiary();

  const {
    mutate: editAllocation,
    isSuccess: isEditSuccess,
    isError: isEditError
  } = useBondAllocationBulkEdit();

  const allocations = useMemo(() => {
    if (!isLoading && asset && asset?.allocations.length > 0) {
      return asset?.allocations.map(allocation => ({
        allocationId: allocation.id,
        beneficiaryId: allocation.beneficiaryId,
        beneficiaryName: `${allocation.beneficiary.beneficiaryFirstName} ${
          allocation.beneficiary.beneficiaryMiddleName || ''
        } ${allocation.beneficiary.beneficiaryLastName}`,
        emailAddress: allocation?.beneficiary?.emailAddress,
        relationship: StringFormater(allocation?.beneficiary?.relationship),
        guardianName: allocation?.beneficiary?.children?.guardian
          ? StringFormater(
              `${allocation?.beneficiary?.children?.guardian
                ?.guardianFirstName} ${
                allocation?.beneficiary?.children?.guardian
                  ?.guardianMiddleName || ''
              } ${allocation?.beneficiary?.children?.guardian
                ?.guardianLastName}`
            )
          : 'N/A',
        allottedPercentage: allocation.allottedPercentage ?? 0
      }));
    } else {
      return [
        {
          allocationId: null,
          beneficiaryId: null,
          beneficiaryName: '',
          emailAddress: '',
          relationship: '',
          guardianName: '',
          allottedPercentage: 0
        }
      ];
    }
  }, [asset, isLoading]);

  const formik = useFormik({
    initialValues: {
      allocations
    },
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      const allocations = values.allocations.map(allocation => ({
        allocationId: allocation.allocationId,
        allottedPercentage: allocation.allottedPercentage
      }));

      onEditAllocationSubmit({ allocations });
    }
  });

  const onEditAllocationSubmit = (values: BeneficiariesAllocationDto) => {
    editAllocation(values);
  };

  const handleDirtyForm = (
    allocation: { [key: string]: any },
    index: number
  ) => {
    const isDirty =
      allocation.allottedPercentage !==
      asset?.allocations[index].allottedPercentage;

    if (!isDirty && dirtyFields.has(allocation.allocationId)) {
      dirtyFields.delete(allocation.allocationId);
    } else {
      dirtyFields.add(allocation.allocationId);
    }
    setDirtyFields(dirtyFields);
  };

  const handleUnassign = () => {
    setIsSubmitting(true);
    mutate({
      allocationId: selectedAllocationId!
    });
  };

  const handleReset = useCallback(() => {
    setDirtyFields(new Set());
    formik.resetForm();
    formik.values.allocations.map((_, index) => {
      formik.setFieldValue(
        `allocations[${index}].allottedPercentage`,
        asset?.allocations[index].allottedPercentage
      );
    });
  }, [asset?.allocations, formik]);

  useEffect(() => {
    if (isEditSuccess && !isEditError) {
      refetch();
      setIsSubmitting(false);
      setDirtyFields(new Set());
      toast(<Alert message="Allocation Successfully Updated" type="success" />);
    } else if (isEditError) {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
      setSelectedAllocationId(null);
      setDirtyFields(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditSuccess, isEditError, refetch]);

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
      setSelectedAllocationId(null);
      refetch();
      toast(<Alert message="Asset Successfully Removed" type="success" />);
    } else if (isError) {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
      setSelectedAllocationId(null);
    }
  }, [isSuccess, isError, refetch]);

  if (isLoading && isFetched) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="my-4 flex items-center justify-between font-circular font-bold text-[#FF6600]">
        <span className="text-xl">Assets</span>
        <Link to="/dashboard/assets" className="flex items-center gap-4">
          <Icon name={Icons.ArrowLeft} />
          <span className="text-base">Back to Assets</span>
        </Link>
      </div>
      {asset && (
        <div className="mt-4 flex flex-col">
          <form onSubmit={formik.handleSubmit}>
            <div className="h-fit w-full rounded-md bg-white shadow-sm">
              <div className="border-b border-[#E5E5E5]">
                <div className="flex items-center justify-between p-3 px-5">
                  <div className="flex items-center gap-5">
                    <div className="flex items-center gap-2">
                      <Icon
                        name={Icons.Bank}
                        fill="#6466C3"
                        width={29}
                        height={27}
                      />
                      <span className="text-xl font-medium text-[#34475E]">
                        {asset.issuerName} - {asset.ISINNo}
                      </span>
                    </div>

                    <div className="mr-9 flex items-center gap-2">
                      <CircleProgressBar
                        progress={asset.remainingAlloted}
                        width={30}
                        height={50}
                      />
                      <div className="flex items-center gap-1 text-base text-[#868686]">
                        <span>{asset.remainingAlloted}%</span>
                        <span>Remaining</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="outline"
                      size="custom"
                      className="place-self-end rounded border-[#FF7A00] px-8 py-3 text-center text-xs font-medium text-[#FF7A00] disabled:border disabled:border-[#D7D7D7] disabled:bg-[#FFFFFF] disabled:text-[#D7D7D7]"
                      label="Discard"
                      disabled={dirtyFields.size === 0 || isSubmitting}
                      loading={isSubmitting || isLoading}
                      type="button"
                      onClick={() => handleReset()}
                    ></Button>
                    <Button
                      variant="outline"
                      size="custom"
                      className="place-self-end rounded border-[#FF7A00] bg-[#FF7A00] px-8 py-3 text-center text-xs font-medium text-white disabled:border-none disabled:bg-[#D7D7D7]"
                      label="Save Changes"
                      onClick={() => {
                        // setSelectedAssetId(asset.id);
                      }}
                      disabled={dirtyFields.size === 0 || isSubmitting}
                      loading={isSubmitting || isLoading}
                      type="submit"
                    ></Button>
                  </div>
                </div>
              </div>

              <div className="mx-8 mt-8">
                <p className="font-circular text-xl font-medium text-[#353535]">
                  Beneficiary List
                </p>

                {asset.allocations.length > 0 ? (
                  <div className="mt-8 overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-500">
                      <thead className="text-xs text-[#1F4173]">
                        <tr>
                          <th scope="col" className="py-3">
                            <span className="sr-only">Image</span>
                          </th>
                          <th scope="col" className="px-6 py-3">
                            Name
                          </th>
                          <th scope="col" className="px-6 py-3">
                            E-mail address
                          </th>
                          <th scope="col" className="px-6 py-3">
                            Relationship
                          </th>
                          <th scope="col" className="px-6 py-3">
                            Guardian
                          </th>
                          <th scope="col" className="px-6 py-3 text-center">
                            Allocation
                          </th>
                          <th scope="col" className="px-6 py-3">
                            <span className="sr-only">Action</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {formik.values.allocations.map((allocation, index) => (
                          <tr
                            className="border-b text-xs font-normal text-[#353535]"
                            key={allocation?.allocationId}
                          >
                            <td className="">
                              <img
                                src={male}
                                className="w-9 max-w-full md:w-9"
                              />
                            </td>
                            <td className="px-6 py-4 ">
                              {allocation?.beneficiaryName}
                            </td>
                            <td className="px-6 py-4 ">
                              {allocation?.emailAddress}
                            </td>
                            <td className="px-6 py-4 ">
                              {allocation?.relationship}
                            </td>
                            <td className="px-6 py-4 ">
                              {allocation?.guardianName}
                            </td>
                            <td className="flex items-center justify-center px-6 py-4">
                              <div className="mx-auto flex max-w-xs items-center">
                                <div className="relative flex max-w-[8rem] items-center">
                                  <button
                                    type="button"
                                    disabled={
                                      allocation.allottedPercentage === 1
                                    }
                                    onClick={() => {
                                      formik.setFieldValue(
                                        `allocations[${index}].allottedPercentage`,
                                        --allocation.allottedPercentage
                                      );
                                      handleDirtyForm(allocation, index);
                                    }}
                                    className="h-7 rounded-s border border-[#B3B3B3] p-3 focus:outline-none focus:ring-2 focus:ring-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:focus:ring-gray-700"
                                  >
                                    <Icon name={Icons.Minus} />
                                  </button>
                                  <input
                                    type="text"
                                    className="block h-7 w-full border-x-0 border-[#B3B3B3] py-2.5 text-center text-[12px] font-normal text-[#353535] focus:border-none focus:ring-[#B3B3B3]"
                                    placeholder={`${allocation.allottedPercentage}%`}
                                    required
                                    disabled
                                  />
                                  <button
                                    onClick={() => {
                                      formik.setFieldValue(
                                        `allocations[${index}].allottedPercentage`,
                                        ++allocation.allottedPercentage
                                      );
                                      handleDirtyForm(allocation, index);
                                    }}
                                    type="button"
                                    className="h-7 rounded-e border border-[#B3B3B3] p-2 focus:outline-none focus:ring-2 focus:ring-gray-100"
                                  >
                                    <Icon name={Icons.Addition} />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                type="button"
                                className="font-medium text-red-600 hover:underline dark:text-red-500"
                                onClick={() => {
                                  setSelectedAllocationId(
                                    allocation.allocationId
                                  );
                                  setShowAskToConfirm(true);
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mt-10">
                    <AlertNotification
                      type="success"
                      message="The designated beneficiary for your asset will be displayed here. Additionally, you can utilize the button below to include a new beneficiary."
                    />
                  </div>
                )}

                <div className="py-32 font-circular text-base font-medium text-[#FF6600]">
                  <Link
                    to={`/dashboard/assets/bond/beneficiary/${bondId}`}
                    type="button"
                  >
                    <div className="flex items-center gap-3 ">
                      <Icon name={Icons.Add} width={20} height={20} /> Add new
                      Beneficiary
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content={`Are you sure you want to remove the user? This can't be undone. Confirm if you want to continue.`}
          onConfirm={() => {
            handleUnassign();
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
